import React, { useState, useRef, useEffect } from 'react';
import {
  SpeakerWaveIcon,
  PauseIcon,
  PlayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const AudioPlayer = ({ script, audioUrl, label = 'Luaj Audion e Hören' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  const stop = () => {
    if (audioUrl && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } else {
      window.speechSynthesis?.cancel();
    }
    setIsPlaying(false);
  };

  const play = () => {
    if (audioUrl && audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.play();
      setIsPlaying(true);
      return;
    }

    if (!window.speechSynthesis) {
      alert('Shfletuesi juaj nuk mbështet zërin. Ju lutem lexoni transkriptin.');
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(script);
    utterance.lang = 'de-DE';
    utterance.rate = playbackRate * 0.92;

    const voices = window.speechSynthesis.getVoices();
    const germanVoice = voices.find((v) => v.lang.startsWith('de') && !v.name.includes('Google') && v.localService) ||
                        voices.find((v) => v.lang.startsWith('de'));
    if (germanVoice) utterance.voice = germanVoice;

    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => {
      setIsPlaying(false);
      setPlayCount((c) => c + 1);
    };
    utterance.onerror = () => setIsPlaying(false);

    window.speechSynthesis.speak(utterance);
  };

  const changeRate = (rate) => {
    setPlaybackRate(rate);
    if (audioUrl && audioRef.current) {
      audioRef.current.playbackRate = rate;
    } else if (isPlaying) {
      // Re-trigger with new rate
      play();
    }
  };

  return (
    <div className="bg-zinc-900 text-white rounded-2xl p-4 shadow-md space-y-3 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {audioUrl ? (
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={() => {
                setIsPlaying(false);
                setPlayCount((c) => c + 1);
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              className="hidden"
            />
          ) : null}

          <button
            type="button"
            onClick={isPlaying ? stop : play}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm ${
              isPlaying
                ? 'bg-amber-500 hover:bg-amber-600 text-zinc-950 animate-pulse'
                : 'bg-yellow-400 hover:bg-yellow-300 text-zinc-950'
            }`}
          >
            {isPlaying ? (
              <>
                <PauseIcon className="w-4 h-4 text-zinc-950" /> Ndalo
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4 text-zinc-950" /> {label}
              </>
            )}
          </button>

          {playCount > 0 && (
            <span className="text-xs text-zinc-400 flex items-center gap-1">
              <ArrowPathIcon className="w-3.5 h-3.5" /> Dëgjuar {playCount}×
            </span>
          )}
        </div>

        {/* Speed Controls */}
        <div className="flex items-center gap-1 bg-zinc-800 p-1 rounded-lg text-xs font-semibold">
          <span className="text-zinc-400 px-1 text-[11px]">Shpejtësia:</span>
          {[0.8, 1.0, 1.2].map((rate) => (
            <button
              key={rate}
              type="button"
              onClick={() => changeRate(rate)}
              className={`px-2 py-0.5 rounded transition-colors ${
                playbackRate === rate
                  ? 'bg-yellow-400 text-zinc-950 font-bold'
                  : 'text-zinc-300 hover:text-white'
              }`}
            >
              {rate}x
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px] text-zinc-400 border-t border-zinc-800 pt-2">
        <span className="flex items-center gap-1">
          <SpeakerWaveIcon className="w-3.5 h-3.5 text-yellow-400" />
          Këshillë: Në Goethe B2 audio luhet vetëm 1 deri 2 herë.
        </span>
        {isPlaying && (
          <span className="text-yellow-400 font-medium animate-pulse">
            ● Duke u luajtur...
          </span>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
